import React, { useState, useEffect, useRef } from 'react';
import { Map as MapLibreMap, NavigationControl, Marker as MlMarker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import L from 'leaflet';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom beach marker icon
const beachIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="15" fill="#FF7F50" stroke="#fff" stroke-width="2"/>
      <circle cx="16" cy="12" r="3" fill="white"/>
      <path d="M8 24 Q16 18 24 24" stroke="white" stroke-width="2" fill="none"/>
      <text x="16" y="28" text-anchor="middle" fill="white" font-family="Arial" font-size="8" font-weight="bold">BEACH</text>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const MapManagement = () => {
  const { user } = useAuth();
  const [beaches, setBeaches] = useState([]);
  const [mapConfig, setMapConfig] = useState({
    center_latitude: 39.7496,
    center_longitude: -74.1039,
    default_zoom: 13,
    rotation: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [selectedBeach, setSelectedBeach] = useState(null);
  const [editingCoords, setEditingCoords] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load beaches with coordinates
      const beachesResponse = await apiClient.get('/api/public/beaches/coordinates');
      setBeaches(beachesResponse.data);

      // Load map configuration
      const configResponse = await apiClient.get('/api/public/map-config');
      setMapConfig(configResponse.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      showMessage('Failed to load map data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleBeachCoordinateUpdate = async (beachId, latitude, longitude) => {
    setSaving(true);
    try {
      const currentZoom = (() => {
        try { return Number(mapRef.current?.getZoom() || mapConfig.default_zoom || 15); } catch (e) { return 15; }
      })();
      await apiClient.put(`/api/beaches/${beachId}/coordinates`, {
        latitude,
        longitude,
        zoom_level: currentZoom
      });
      showMessage('Beach coordinates updated successfully', 'success');

      // Update local state
      setBeaches(prev => prev.map(beach =>
        beach.id === beachId
          ? { ...beach, latitude, longitude, zoom_level: currentZoom }
          : beach
      ));
    } catch (error) {
      console.error('Failed to update beach coordinates:', error);
      showMessage('Failed to update beach coordinates', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMapConfigUpdate = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/map-config', mapConfig);
      showMessage('Map configuration updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update map configuration:', error);
      showMessage('Failed to update map configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Map click handled via MapLibre event registration (see effects below)

  const MapClickHandler = null; // Not used with MapLibre

  const markersRef = useRef({});

  // Initialize MapLibre
  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;
    const map = new MapLibreMap({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors'
          }
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }]
      },
      center: [mapConfig.center_longitude, mapConfig.center_latitude],
      zoom: mapConfig.default_zoom,
      bearing: mapConfig.rotation || 0,
      pitch: 0,
      hash: false,
      interactive: true
    });
    map.addControl(new NavigationControl({ visualizePitch: false }), 'top-left');
    mapRef.current = map;

    // Initial markers with custom icon and label
    beaches.forEach((b) => {
      if (b.latitude && b.longitude) {
        const el = document.createElement('div');
        el.style.display = 'flex';
        el.style.flexDirection = 'column';
        el.style.alignItems = 'center';
        el.style.transform = 'translateY(2px)';
        const img = document.createElement('img');
        img.src = '/stand.svg';
        img.alt = 'Beach';
        img.style.width = '28px';
        img.style.height = '28px';
        const color = (b.flag_status || 'black').toLowerCase();
        const colorMap = {
          black: 'brightness(0) saturate(100%)',
          green: 'invert(45%) sepia(97%) saturate(414%) hue-rotate(83deg) brightness(90%) contrast(95%)',
          yellow: 'invert(77%) sepia(86%) saturate(527%) hue-rotate(356deg) brightness(101%) contrast(101%)',
          red: 'invert(22%) sepia(85%) saturate(4059%) hue-rotate(346deg) brightness(85%) contrast(102%)'
        };
        img.style.filter = colorMap[color] || colorMap.black;
        const label = document.createElement('span');
        label.textContent = b.name || '';
        label.style.marginTop = '2px';
        label.style.fontSize = '12px';
        label.style.fontWeight = '600';
        label.style.color = '#1f2937';
        label.style.padding = '1px 4px';
        label.style.background = 'rgba(255,255,255,0.9)';
        label.style.border = '1px solid rgba(0,0,0,0.15)';
        label.style.borderRadius = '4px';
        el.appendChild(img);
        el.appendChild(label);
        el._label = label;
        const m = new MlMarker({ element: el, anchor: 'bottom', draggable: true })
          .setLngLat([b.longitude, b.latitude])
          .addTo(map);
        m.on('dragend', () => {
          const pos = m.getLngLat();
          handleBeachCoordinateUpdate(b.id, pos.lat, pos.lng);
        });
        markersRef.current[b.id] = m;
      }
    });

    return () => {
      try { map.remove(); } catch (e) {}
      mapRef.current = null;
    };
  }, [mapConfig.center_latitude, mapConfig.center_longitude, mapConfig.default_zoom]);

  // Sync rotation and center on config change
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setBearing(Number(mapConfig.rotation || 0));
    mapRef.current.setCenter([mapConfig.center_longitude, mapConfig.center_latitude]);
    mapRef.current.setZoom(mapConfig.default_zoom);
  }, [mapConfig.rotation, mapConfig.center_latitude, mapConfig.center_longitude, mapConfig.default_zoom]);

  // Update markers when beaches change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Remove orphaned markers
    Object.keys(markersRef.current).forEach((id) => {
      if (!beaches.find((b) => String(b.id) === String(id))) {
        try { markersRef.current[id].remove(); } catch (e) {}
        delete markersRef.current[id];
      }
    });
    // Add/update markers
    beaches.forEach((b) => {
      if (!b.latitude || !b.longitude) return;
      const existing = markersRef.current[b.id];
      if (existing) {
        existing.setLngLat([b.longitude, b.latitude]);
        try {
          const el = existing.getElement && existing.getElement();
          if (el && el._label) { el._label.textContent = b.name || ''; }
          if (el && el.querySelector('img')) {
            const color = (b.flag_status || 'black').toLowerCase();
            const colorMap = {
              black: 'brightness(0) saturate(100%)',
              green: 'invert(45%) sepia(97%) saturate(414%) hue-rotate(83deg) brightness(90%) contrast(95%)',
              yellow: 'invert(77%) sepia(86%) saturate(527%) hue-rotate(356deg) brightness(101%) contrast(101%)',
              red: 'invert(22%) sepia(85%) saturate(4059%) hue-rotate(346deg) brightness(85%) contrast(102%)'
            };
            el.querySelector('img').style.filter = colorMap[color] || colorMap.black;
          }
        } catch (e) {}
      } else {
        const el = document.createElement('div');
        el.style.display = 'flex';
        el.style.flexDirection = 'column';
        el.style.alignItems = 'center';
        el.style.transform = 'translateY(2px)';
        const img = document.createElement('img');
        img.src = '/stand.svg';
        img.alt = 'Beach';
        img.style.width = '28px';
        img.style.height = '28px';
        {
          const color = (b.flag_status || 'black').toLowerCase();
          const colorMap = {
            black: 'brightness(0) saturate(100%)',
            green: 'invert(45%) sepia(97%) saturate(414%) hue-rotate(83deg) brightness(90%) contrast(95%)',
            yellow: 'invert(77%) sepia(86%) saturate(527%) hue-rotate(356deg) brightness(101%) contrast(101%)',
            red: 'invert(22%) sepia(85%) saturate(4059%) hue-rotate(346deg) brightness(85%) contrast(102%)'
          };
          img.style.filter = colorMap[color] || colorMap.black;
        }
        const label = document.createElement('span');
        label.textContent = b.name || '';
        label.style.marginTop = '2px';
        label.style.fontSize = '12px';
        label.style.fontWeight = '600';
        label.style.color = '#1f2937';
        label.style.padding = '1px 4px';
        label.style.background = 'rgba(255,255,255,0.9)';
        label.style.border = '1px solid rgba(0,0,0,0.15)';
        label.style.borderRadius = '4px';
        el.appendChild(img);
        el.appendChild(label);
        el._label = label;
        const m = new MlMarker({ element: el, anchor: 'bottom', draggable: true })
          .setLngLat([b.longitude, b.latitude])
          .addTo(map);
        m.on('dragend', () => {
          const pos = m.getLngLat();
          handleBeachCoordinateUpdate(b.id, pos.lat, pos.lng);
        });
        markersRef.current[b.id] = m;
      }
    });
  }, [beaches]);

  // Rebind click handler for set-location flow
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handler = (e) => {
      if (selectedBeach && editingCoords) {
        handleBeachCoordinateUpdate(selectedBeach.id, e.lngLat.lat, e.lngLat.lng);
        setSelectedBeach(null);
        setEditingCoords(false);
      }
    };
    map.on('click', handler);
    return () => { try { map.off('click', handler); } catch (e) {} };
  }, [selectedBeach, editingCoords]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading map data...</p>
      </div>
    );
  }

  return (
    <div className="map-management">
      <div className="page-header">
        <h1 className="page-title">Map Management</h1>
        <p className="page-subtitle">Configure beach locations and map settings</p>
      </div>

      {message && (
        <div className={`alert alert-${messageType}`}>
          {message}
        </div>
      )}

      <div className="map-management-container">
        {/* Map Configuration Panel */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Map Settings</h2>
          </div>
          <div className="form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Center Latitude</label>
                <input
                  type="number"
                  step="0.000001"
                  className="form-control"
                  value={mapConfig.center_latitude}
                  onChange={(e) => setMapConfig(prev => ({
                    ...prev,
                    center_latitude: parseFloat(e.target.value) || 0
                  }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Center Longitude</label>
                <input
                  type="number"
                  step="0.000001"
                  className="form-control"
                  value={mapConfig.center_longitude}
                  onChange={(e) => setMapConfig(prev => ({
                    ...prev,
                    center_longitude: parseFloat(e.target.value) || 0
                  }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Default Zoom</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className="form-control"
                  value={mapConfig.default_zoom}
                  onChange={(e) => setMapConfig(prev => ({
                    ...prev,
                    default_zoom: parseInt(e.target.value) || 13
                  }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Rotation (degrees)</label>
                <input
                  type="number"
                  min="-180"
                  max="180"
                  step="0.1"
                  className="form-control"
                  value={mapConfig.rotation}
                  onChange={(e) => setMapConfig(prev => ({
                    ...prev,
                    rotation: parseFloat(e.target.value) || 0
                  }))}
                />
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={handleMapConfigUpdate}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? 'Saving...' : 'Update Map Settings'}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  if (!mapRef.current) return;
                  mapRef.current.setBearing(Number(mapConfig.rotation || 0));
                }}
                className="btn btn-secondary"
                style={{ marginLeft: '8px' }}
              >
                Preview Rotation
              </button>
            </div>
          </div>
        </div>

        {/* Beach Locations Panel */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Beach Locations</h2>
            <p className="card-subtitle">Click "Set Location" to place or move beach markers on the map</p>
          </div>
          <div className="beach-list">
            {beaches.map(beach => (
              <div key={beach.id} className="beach-list-item">
                <div className="beach-info">
                  <h4>{beach.name}</h4>
                  {beach.description && <p>{beach.description}</p>}
                  {Number.isFinite(Number(beach.latitude)) && Number.isFinite(Number(beach.longitude)) ? (
                    <div className="coordinates">
                      <small>
                        Lat: {Number(beach.latitude).toFixed(6)}, Lng: {Number(beach.longitude).toFixed(6)}
                      </small>
                    </div>
                  ) : (
                    <div className="no-coordinates">
                      <small className="text-muted">No coordinates set</small>
                    </div>
                  )}
                </div>
                <div className="beach-actions">
                  <button
                    onClick={() => {
                      setSelectedBeach(beach);
                      setEditingCoords(true);
                      if (mapRef.current && beach.latitude && beach.longitude) {
                        mapRef.current.setCenter([beach.longitude, beach.latitude]);
                        mapRef.current.setZoom(15);
                      }
                    }}
                    disabled={editingCoords && selectedBeach?.id === beach.id}
                    className="btn btn-sm btn-primary"
                  >
                    {editingCoords && selectedBeach?.id === beach.id ? 'Click on Map' : 'Set Location'}
                  </button>
                  {editingCoords && selectedBeach?.id === beach.id && (
                    <button
                      onClick={() => {
                        setSelectedBeach(null);
                        setEditingCoords(false);
                      }}
                      className="btn btn-sm btn-secondary"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Interactive Map</h2>
          <p className="card-subtitle">
            {editingCoords ? `Click on the map to set location for ${selectedBeach?.name}` : 'Drag markers to reposition beaches'}
          </p>
        </div>
        <div className="map-container">
          <div ref={mapContainerRef} style={{ height: '500px', width: '100%' }} />
        </div>
      </div>

      <style jsx>{`
        .map-management {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .map-management-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .beach-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .beach-list-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          margin-bottom: 0.5rem;
          background: #f9f9f9;
        }

        .beach-info h4 {
          margin: 0 0 0.25rem 0;
          color: #2c5f7f;
        }

        .beach-info p {
          margin: 0;
          font-size: 0.9rem;
          color: #666;
        }

        .coordinates, .no-coordinates {
          margin-top: 0.25rem;
        }

        .beach-actions {
          display: flex;
          gap: 0.5rem;
        }

        .map-container {
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid #e0e0e0;
        }

        .beach-popup h4 {
          margin: 0 0 0.5rem 0;
          color: #2c5f7f;
        }

        .beach-popup p {
          margin: 0.25rem 0;
          font-size: 0.9rem;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem;
          text-align: center;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .map-management-container {
            grid-template-columns: 1fr;
          }

          .map-container {
            height: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default MapManagement;
